import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Smartphone, Loader2, ArrowLeft, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import api from '@/lib/api';
import Logo from '@/components/Logo';

export default function PhoneLogin() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get('ref') || '').toUpperCase();
  const [step, setStep] = useState('phone'); // phone | otp
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [demoHint, setDemoHint] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [refMeta, setRefMeta] = useState(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => {
    if (!refCode) return;
    api.get(`/referrals/validate/${refCode}`)
      .then(({ data }) => setRefMeta(data))
      .catch(() => setRefMeta(null));
  }, [refCode]);

  const requestOtp = async () => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/otp/request', { phone: digits, name, email: email.trim() || null });
      setDemoHint(data?.demo_hint || '');
      toast.success(data?.mode === 'demo' && data?.demo_hint ? data.demo_hint : 'OTP sent successfully');
      setStep('otp');
      setResendIn(30);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send OTP');
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      const digits = phone.replace(/\D/g, '').slice(-10);
      const { data } = await api.post('/auth/otp/verify', {
        phone: digits, otp, name, email: email.trim() || null, referrer_code: refMeta ? refCode : null,
      });
      localStorage.setItem('bill4pe_token', data.token);
      localStorage.setItem('bill4pe_user', JSON.stringify(data.user));
      toast.success(`Welcome${data.user?.name ? ', ' + data.user.name.split(' ')[0] : ''}!`);
      // Hard reload so AuthProvider picks up the new user
      window.location.assign('/app');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-navy text-white grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12">
        <Link to="/" className="self-start bg-white inline-flex items-center p-3 rounded-xl">
          <img src="/logo.png?v=7" alt="Bil4Pe — The Intelligent Billing" className="h-20 w-auto object-contain" />
        </Link>
        <div>
          <div className="font-display text-5xl font-bold leading-tight">Sign in with your phone.</div>
          <p className="text-white/60 mt-4 max-w-sm">Secure phone verification for BILL4PE users.</p>
        </div>
        <div className="text-xs text-white/40">© 2026 BILL4PE · www.bill4pe.com</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-white text-navy relative">
        <Link
          to="/"
          data-testid="back-to-landing-btn"
          className="press-down absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-slate-500 hover:text-navy hover:bg-slate-100 z-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>
        <div className="w-full max-w-sm">
          <Link to="/" className="md:hidden mb-8 bg-white inline-flex items-center p-2 rounded-xl">
            <img src="/logo.png?v=7" alt="Bil4Pe — The Intelligent Billing" className="h-16 w-auto object-contain" />
          </Link>

          {step === 'phone' && (
            <>
              <h1 className="font-display font-bold text-3xl">Phone login</h1>
              <p className="text-sm text-slate-500 mt-1">We'll send a 6-digit OTP to verify.</p>

              {refMeta && (
                <div className="mt-4 flex items-start gap-3 rounded-2xl border border-brand/30 bg-brand/5 p-3" data-testid="referral-banner">
                  <div className="w-9 h-9 rounded-xl bg-brand text-white grid place-items-center shrink-0">
                    <Gift className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-navy text-sm">{refMeta.referrer_name} invited you</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      You'll both get <span className="font-bold text-brand">₹{refMeta.bonus}</span> wallet credit on signup.
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Name (optional)</label>
                  <Input
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 h-12 rounded-xl border-soft"
                    data-testid="otp-name-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Email (required for first signup)</label>
                  <Input
                    type="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 h-12 rounded-xl border-soft"
                    data-testid="otp-email-input"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Existing phone users may leave this blank.</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Mobile number</label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-slate-500">+91</span>
                    <Input
                      type="tel" maxLength={10}
                      placeholder="98765 43210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-12 h-12 rounded-xl border-soft font-mono tracking-widest"
                      data-testid="otp-phone-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={requestOtp} disabled={loading}
                  className="press-down w-full h-12 bg-brand text-white hover:bg-[#1858CC] rounded-full font-semibold"
                  data-testid="otp-request-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
                  Send OTP
                </Button>
              </div>
              <div className="mt-6 text-sm text-slate-500 text-center">
                Prefer email?{' '}
                <Link to="/login" className="text-navy font-semibold underline" data-testid="link-email-login">
                  Sign in with email
                </Link>
              </div>
            </>
          )}

          {step === 'otp' && (
            <>
              <h1 className="font-display font-bold text-3xl">Enter OTP</h1>
              <p className="text-sm text-slate-500 mt-1">
                Sent to <span className="font-mono text-navy font-semibold">+91 {phone}</span>
              </p>
              {demoHint && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-lime/30 text-navy px-2 py-1 rounded-full font-bold">
                  <Smartphone className="w-3 h-3" /> {demoHint}
                </div>
              )}

              <div className="mt-8 flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg font-mono" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={verifyOtp} disabled={loading || otp.length !== 6}
                className="press-down w-full h-12 mt-7 bg-brand text-white hover:bg-[#1858CC] rounded-full font-semibold"
                data-testid="otp-verify-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verify & continue
              </Button>

              <div className="mt-5 flex items-center justify-between text-sm">
                <button
                  className="text-slate-500 underline"
                  onClick={() => { setStep('phone'); setOtp(''); }}
                  data-testid="otp-change-number"
                >Change number</button>
                <button
                  className="text-navy font-semibold disabled:text-slate-400 disabled:no-underline underline"
                  disabled={resendIn > 0 || loading}
                  onClick={requestOtp}
                  data-testid="otp-resend"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
